// Version string arithmetic for the revision log.
//
// Shape: [prefix][major].[minor] — a35.009, b01.000, 01.000. The prefix marks environment
// maturity (a = alpha preprod, b = beta preprod, none = prod) and is carried through
// untouched. The scheme itself is documented in the Revision Log help entry.
//
// WHY THIS IS SHARED: the Rev Log add dialog and the Dev Plan Complete Step wizard both
// compute a next version, and each carried its own copy. Both dropped zero padding on a
// MAJOR bump — parseInt then re-concatenate — while padding correctly on a minor. It never
// showed because an aXX major is already two digits (a35 → a36), but the day pre-production
// resets to b01.000 the first step completion would have produced b2.000 instead of b02.000,
// and prod's 01.000 would have gone to 2.000.
//
// Both components keep the width they arrive with. padStart only pads and never truncates,
// so a genuine width increase (b99.000 → b100.000) is preserved rather than clipped.

/**
 * Next version string from a base and bump type.
 *
 * A base that does not match the expected shape is returned unchanged — callers that POST
 * the result directly should treat "unchanged" as "do not post", since the rev log would
 * otherwise get a duplicate version. In practice the base always comes from our own rev log.
 *
 * @param {string} base  current version, e.g. "a35.009"
 * @param {'major'|'minor'|'patch'} type
 * @returns {string}
 */
export const suggestNextVersion = (base, type) => {
  if (!base) return type === 'major' ? 'a1.000' : 'a0.001';
  const match = /^([a-zA-Z]*)(\d+)\.(\d+)$/.exec(base);
  if (!match) return base;
  const [, prefix, major, minor] = match;

  if (type === 'major') {
    const nextMajor = String(parseInt(major, 10) + 1).padStart(major.length, '0');
    return `${prefix}${nextMajor}.${'0'.repeat(minor.length)}`;
  }

  // minor and patch both bump the minor: patch is a label on the sequence, not a third segment.
  const nextMinor = String(parseInt(minor, 10) + 1).padStart(minor.length, '0');
  return `${prefix}${major}.${nextMinor}`;
};

export default suggestNextVersion;
